/**
 * Delete Location(s) Confirmation Modal
 * Same layout and styling as DeleteFacilityModal, with location-specific copy.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { X, AlertTriangle } from 'lucide-react';

export const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  onCancel,
  count,
  locationNames = [],
}) => {
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const handleClose = () => {
    onCancel?.();
    onClose();
  };

  if (!isOpen) return null;

  const isSingular = count === 1;
  const title = isSingular ? 'Delete Area' : 'Delete Areas';
  const deleteButtonLabel = isSingular ? 'Delete Area' : 'Delete Areas';

  // Show up to 5 names; then "and N more" if needed
  const displayNames = locationNames.slice(0, 5);
  const remainingCount = locationNames.length > 5 ? locationNames.length - 5 : 0;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-[rgba(184,182,182,0.5)] z-50"
        onClick={handleClose}
        aria-hidden
      />

      {/* Modal */}
      <div
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-[10px] px-8 py-8 w-[90vw] max-w-[540px] z-[60] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="delete-locations-title"
        aria-modal="true"
      >
        <div className="flex flex-col gap-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" aria-hidden />
              </div>
              <h2
                id="delete-locations-title"
                className="font-['Roboto:Bold',sans-serif] font-bold text-[24px] text-black"
              >
                {title}
              </h2>
            </div>

            <button
              type="button"
              onClick={handleClose}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-[#F2F4F8] hover:bg-gray-300 transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 text-[#1C1B1F]" />
            </button>
          </div>

          {/* Location(s) summary - shared info card style */}
          <div className="rounded-2xl border border-[#A5D8FF] bg-[#E8F4FC] p-4">
            {displayNames.length > 0 ? (
              <>
                <p className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[16px] text-[#333C4D]">
                  {isSingular ? displayNames[0] : `${count} area${count !== 1 ? 's' : ''} selected`}
                </p>
                {!isSingular && displayNames.length > 0 && (
                  <ul className="font-['Roboto:Regular',sans-serif] text-[14px] text-[#333C4D] mt-2 list-disc list-inside space-y-0.5 leading-relaxed">
                    {displayNames.map((name, idx) => (
                      <li key={idx}>{name}</li>
                    ))}
                    {remainingCount > 0 && (
                      <li className="font-['Roboto:Medium',sans-serif]">
                        and {remainingCount} more
                      </li>
                    )}
                  </ul>
                )}
              </>
            ) : (
              <p className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[16px] text-[#333C4D]">
                {count} area{count !== 1 ? 's' : ''} selected
              </p>
            )}
          </div>

          {/* Warning Message */}
          <div className="space-y-3">
            <p className="font-['Roboto:Medium',sans-serif] text-[15px] text-gray-800 leading-relaxed">
              Are you sure? This will delete{' '}
              {isSingular ? (
                <>
                  <span className="font-['Roboto:Bold',sans-serif] font-bold text-gray-900">
                    {displayNames[0] || 'this area'}
                  </span>{' '}
                </>
              ) : (
                <>
                  <span className="font-['Roboto:Bold',sans-serif] font-bold text-gray-900">
                    {count}
                  </span>{' '}
                  areas
                </>
              )}{' '}
              permanently.
            </p>

            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <p className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[14px] text-amber-900 mb-2">
                The following will also be deleted:
              </p>
              <div className="flex items-center gap-2 ml-2">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-600 shrink-0" />
                <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-amber-800">
                  Associated cleaning standards
                </p>
              </div>
            </div>

            <p className="font-['Roboto:Medium',sans-serif] text-[14px] text-gray-600 leading-relaxed flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden />
              This action cannot be undone.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="border-2 border-gray-300 bg-white text-gray-700 rounded-lg px-6 py-2.5 font-['Roboto:SemiBold',sans-serif] font-semibold text-[15px] hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleConfirm}
              className="bg-red-600 text-white rounded-lg px-6 py-2.5 font-['Roboto:SemiBold',sans-serif] font-semibold text-[15px] hover:bg-red-700 transition-colors"
            >
              {deleteButtonLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

DeleteConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  count: PropTypes.number.isRequired,
  locationNames: PropTypes.arrayOf(PropTypes.string),
};
